import React, { useState, useEffect, useMemo } from 'react';
import { pricesAPI } from '@/lib/api';
import { 
  DollarSign, TrendingUp, TrendingDown, Minus, Search, 
  Plus, Calendar, Package, Filter, ChevronDown, ChevronUp,
  Trash2, BarChart3, ArrowUpRight, ArrowDownRight, Pencil, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import SupplierPriceComparison from '@/components/prices/SupplierPriceComparison';

const AdminPricesPage = () => {
  const [activeTab, setActiveTab] = useState('history'); // 'history' or 'compare'
  const [products, setProducts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [expandedProduct, setExpandedProduct] = useState(null);
  
  // Estado para el modal de agregar precio
  const [priceDialog, setPriceDialog] = useState({ open: false, product: null });
  const [priceForm, setPriceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    price: '',
    supplier: '',
    newSupplier: '',
    useNewSupplier: false,
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  
  // Estado para el modal de editar precio
  const [editDialog, setEditDialog] = useState({ open: false, product: null, entry: null });
  const [editForm, setEditForm] = useState({
    date: '',
    price: '',
    supplier: '',
    newSupplier: '',
    useNewSupplier: false,
    notes: ''
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, [supplierFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsRes, analyticsRes, suppliersRes, allSuppliersRes] = await Promise.all([
        pricesAPI.getAll(supplierFilter !== 'all' ? supplierFilter : null),
        pricesAPI.getAnalytics(),
        pricesAPI.getSuppliers(),
        pricesAPI.getAllSuppliers()
      ]);
      setProducts(productsRes.data);
      setAnalytics(analyticsRes.data);
      setSuppliers(suppliersRes.data);
      setAllSuppliers(allSuppliersRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de precios');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = !searchTerm || 
        product.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.item_code.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [products, searchTerm]);

  const openPriceDialog = (product) => {
    setPriceDialog({ open: true, product });
    setPriceForm({
      date: new Date().toISOString().split('T')[0],
      price: '',
      supplier: product.supplier || '',
      newSupplier: '',
      useNewSupplier: false,
      notes: ''
    });
  };

  const handleAddPrice = async () => {
    const supplierToUse = priceForm.useNewSupplier ? priceForm.newSupplier.trim() : priceForm.supplier;
    
    if (!supplierToUse) {
      toast.error('Selecciona o ingresa un proveedor');
      return;
    }
    if (!priceForm.price || parseFloat(priceForm.price) <= 0) {
      toast.error('Ingresa un precio válido');
      return;
    }

    setSubmitting(true);
    try {
      await pricesAPI.addPrice({
        item_id: priceDialog.product.item_id,
        date: priceForm.date,
        price: parseFloat(priceForm.price),
        supplier: supplierToUse,
        notes: priceForm.notes || null
      });
      toast.success(`Precio registrado para ${priceDialog.product.item_name} (${supplierToUse})`);
      setPriceDialog({ open: false, product: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar precio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePrice = async (entryId, supplier) => {
    if (!window.confirm(`¿Eliminar el precio de ${supplier}?`)) return;
    
    try {
      await pricesAPI.deletePrice(entryId);
      toast.success('Precio eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar precio');
    }
  };

  // Funciones para editar precio
  const openEditDialog = (product, entry) => {
    setEditDialog({ open: true, product, entry });
    setEditForm({
      date: entry.date,
      price: entry.price.toString(),
      supplier: entry.supplier || '',
      newSupplier: '',
      useNewSupplier: false,
      notes: entry.notes || ''
    });
  };

  const handleEditPrice = async () => {
    const supplierToUse = editForm.useNewSupplier ? editForm.newSupplier.trim() : editForm.supplier;
    
    if (!supplierToUse) {
      toast.error('Selecciona o ingresa un proveedor');
      return;
    }
    if (!editForm.price || parseFloat(editForm.price) <= 0) {
      toast.error('Ingresa un precio válido');
      return;
    }

    setEditSubmitting(true);
    try {
      // Usar el ID único de la entrada para actualizar
      await pricesAPI.updatePrice(
        editDialog.entry.id,
        {
          new_date: editForm.date,
          new_price: parseFloat(editForm.price),
          new_supplier: supplierToUse,
          notes: editForm.notes || null
        }
      );
      toast.success('Precio actualizado correctamente');
      setEditDialog({ open: false, product: null, entry: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar precio');
    } finally {
      setEditSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return `$${price.toLocaleString('es-CL')}`;
  };

  const getPriceChangeIcon = (change) => {
    if (change === null || change === undefined) return <Minus className="w-4 h-4 text-slate-400" />;
    if (change > 1) return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (change < -1) return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getPriceChangeColor = (change) => {
    if (change === null || change === undefined) return 'text-slate-500';
    if (change > 1) return 'text-red-600';
    if (change < -1) return 'text-green-600';
    return 'text-slate-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="admin-prices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-primary" />
            Análisis de Precios
          </h1>
          <p className="text-sm text-muted-foreground">
            Sensibilidad y seguimiento de precios de compra
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <BarChart3 className="w-4 h-4 inline mr-2" />
          Historial de Precios
        </button>
        <button
          onClick={() => setActiveTab('compare')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'compare'
              ? 'border-primary text-primary'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-2" />
          Comparar Proveedores
        </button>
      </div>

      {/* Tab: Comparar Proveedores */}
      {activeTab === 'compare' && <SupplierPriceComparison />}

      {/* Tab: Historial de Precios */}
      {activeTab === 'history' && (
        <>
      {/* KPIs de Análisis */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Productos</span>
              <Package className="w-5 h-5 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900 mt-1">{analytics.total_products}</p>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Con Historial</span>
              <BarChart3 className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-600 mt-1">{analytics.products_with_history}</p>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Precio Subió</span>
              <ArrowUpRight className="w-5 h-5 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600 mt-1">{analytics.products_price_up}</p>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Precio Bajó</span>
              <ArrowDownRight className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-600 mt-1">{analytics.products_price_down}</p>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Cambio Promedio</span>
              {analytics.avg_price_change !== null && (
                analytics.avg_price_change > 0 
                  ? <TrendingUp className="w-5 h-5 text-red-500" />
                  : <TrendingDown className="w-5 h-5 text-green-500" />
              )}
            </div>
            <p className={`text-2xl font-bold mt-1 ${
              analytics.avg_price_change > 0 ? 'text-red-600' : 
              analytics.avg_price_change < 0 ? 'text-green-600' : 'text-slate-600'
            }`}>
              {analytics.avg_price_change !== null ? `${analytics.avg_price_change > 0 ? '+' : ''}${analytics.avg_price_change}%` : '-'}
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar producto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-full md:w-64">
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger>
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar por proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {suppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Lista de Productos */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Código</th>
                <th className="px-4 py-3 text-left font-semibold">Producto</th>
                <th className="px-4 py-3 text-left font-semibold">Proveedor</th>
                <th className="px-4 py-3 text-right font-semibold">Precio Actual</th>
                <th className="px-4 py-3 text-right font-semibold">Mín / Máx</th>
                <th className="px-4 py-3 text-right font-semibold">Variación</th>
                <th className="px-4 py-3 text-center font-semibold">Historial</th>
                <th className="px-4 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProducts.map((product) => (
                <React.Fragment key={product.item_id}>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-sm">{product.item_code}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{product.item_name}</p>
                      <p className="text-xs text-slate-400">{product.unit}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md">
                        {product.supplier || 'Sin asignar'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-lg text-slate-900">
                        {formatPrice(product.current_price)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      {product.min_price !== null ? (
                        <span className="text-slate-600">
                          {formatPrice(product.min_price)} / {formatPrice(product.max_price)}
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {getPriceChangeIcon(product.price_change_percent)}
                        <span className={`font-semibold ${getPriceChangeColor(product.price_change_percent)}`}>
                          {product.price_change_percent !== null 
                            ? `${product.price_change_percent > 0 ? '+' : ''}${product.price_change_percent}%`
                            : '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedProduct(expandedProduct === product.item_id ? null : product.item_id)}
                        className="text-slate-600"
                      >
                        {product.price_history.length} registros
                        {expandedProduct === product.item_id 
                          ? <ChevronUp className="w-4 h-4 ml-1" />
                          : <ChevronDown className="w-4 h-4 ml-1" />
                        }
                      </Button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => openPriceDialog(product)}
                        data-testid={`add-price-${product.item_id}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Precio
                      </Button>
                    </td>
                  </tr>
                  
                  {/* Historial expandido */}
                  {expandedProduct === product.item_id && product.price_history.length > 0 && (
                    <tr>
                      <td colSpan="8" className="px-4 py-4 bg-slate-50">
                        <div className="max-h-64 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-slate-500">
                                <th className="px-3 py-2 text-left font-medium">Fecha</th>
                                <th className="px-3 py-2 text-left font-medium">Proveedor</th>
                                <th className="px-3 py-2 text-right font-medium">Precio</th>
                                <th className="px-3 py-2 text-left font-medium">Notas</th>
                                <th className="px-3 py-2 text-center font-medium">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {product.price_history.map((entry, idx) => (
                                <tr key={`${entry.date}-${entry.supplier}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-25'}>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-slate-400" />
                                      {format(new Date(entry.date), 'dd/MM/yyyy', { locale: es })}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md font-medium">
                                      {entry.supplier || 'Sin proveedor'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-right font-semibold">
                                    {formatPrice(entry.price)}
                                  </td>
                                  <td className="px-3 py-2 text-slate-500">
                                    {entry.notes || '-'}
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openEditDialog(product, entry)}
                                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                        data-testid={`edit-price-${product.item_id}-${entry.date}`}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeletePrice(entry.id, entry.supplier)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No se encontraron productos</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog para agregar precio */}
      <Dialog open={priceDialog.open} onOpenChange={(open) => setPriceDialog({ ...priceDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Registrar Precio de Compra
            </DialogTitle>
          </DialogHeader>
          
          {priceDialog.product && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium text-slate-900">{priceDialog.product.item_name}</p>
                <p className="text-sm text-slate-500">{priceDialog.product.item_code} • {priceDialog.product.unit}</p>
                {priceDialog.product.current_price && (
                  <p className="text-sm text-slate-600 mt-1">
                    Último precio: <span className="font-semibold">{formatPrice(priceDialog.product.current_price)}</span>
                  </p>
                )}
              </div>
              
              {/* Selector de Proveedor */}
              <div className="space-y-3">
                <Label>Proveedor *</Label>
                
                {!priceForm.useNewSupplier ? (
                  <div className="space-y-2">
                    <Select 
                      value={priceForm.supplier} 
                      onValueChange={(value) => setPriceForm({ ...priceForm, supplier: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar proveedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {allSuppliers.map(supplier => (
                          <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      type="button"
                      onClick={() => setPriceForm({ ...priceForm, useNewSupplier: true, supplier: '' })}
                      className="text-primary text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Agregar nuevo proveedor
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={priceForm.newSupplier}
                      onChange={(e) => setPriceForm({ ...priceForm, newSupplier: e.target.value })}
                      placeholder="Nombre del nuevo proveedor"
                    />
                    <Button 
                      variant="ghost" 
                      size="sm"
                      type="button"
                      onClick={() => setPriceForm({ ...priceForm, useNewSupplier: false, newSupplier: '' })}
                      className="text-slate-500 text-xs"
                    >
                      Seleccionar proveedor existente
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price-date">Fecha</Label>
                  <Input
                    id="price-date"
                    type="date"
                    value={priceForm.date}
                    onChange={(e) => setPriceForm({ ...priceForm, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="price-value">Precio Neto ($)</Label>
                  <Input
                    id="price-value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={priceForm.price}
                    onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="price-notes">Notas (opcional)</Label>
                <Input
                  id="price-notes"
                  value={priceForm.notes}
                  onChange={(e) => setPriceForm({ ...priceForm, notes: e.target.value })}
                  placeholder="Ej: Precio promocional, descuento por volumen..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceDialog({ open: false, product: null })}>
              Cancelar
            </Button>
            <Button onClick={handleAddPrice} disabled={submitting}>
              {submitting ? 'Guardando...' : 'Guardar Precio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para editar precio */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Editar Precio de Compra
            </DialogTitle>
          </DialogHeader>
          
          {editDialog.product && editDialog.entry && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium text-slate-900">{editDialog.product.item_name}</p>
                <p className="text-sm text-slate-500">{editDialog.product.item_code} • {editDialog.product.unit}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Editando registro del {format(new Date(editDialog.entry.date), 'dd/MM/yyyy', { locale: es })}
                </p>
              </div>
              
              {/* Selector de Proveedor */}
              <div className="space-y-3">
                <Label>Proveedor *</Label>
                
                {!editForm.useNewSupplier ? (
                  <div className="space-y-2">
                    <Select 
                      value={editForm.supplier} 
                      onValueChange={(value) => setEditForm({ ...editForm, supplier: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar proveedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {allSuppliers.map(supplier => (
                          <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      type="button"
                      onClick={() => setEditForm({ ...editForm, useNewSupplier: true, supplier: '' })}
                      className="text-primary text-xs"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Agregar nuevo proveedor
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={editForm.newSupplier}
                      onChange={(e) => setEditForm({ ...editForm, newSupplier: e.target.value })}
                      placeholder="Nombre del nuevo proveedor"
                    />
                    <Button 
                      variant="ghost" 
                      size="sm"
                      type="button"
                      onClick={() => setEditForm({ ...editForm, useNewSupplier: false, newSupplier: '' })}
                      className="text-slate-500 text-xs"
                    >
                      Seleccionar proveedor existente
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-price-date">Fecha</Label>
                  <Input
                    id="edit-price-date"
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-price-value">Precio Neto ($)</Label>
                  <Input
                    id="edit-price-value"
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="edit-price-notes">Notas (opcional)</Label>
                <Input
                  id="edit-price-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Ej: Precio promocional, descuento por volumen..."
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, product: null, entry: null })}>
              Cancelar
            </Button>
            <Button onClick={handleEditPrice} disabled={editSubmitting}>
              {editSubmitting ? 'Guardando...' : 'Actualizar Precio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
};

export default AdminPricesPage;
